import { viewports } from '@telus-uds/system-constants';
import { useViewport } from '../ViewportProvider';
import hasOwnProperty from './hasOwnProperty';
const hasResponsiveProperties = objectProp => viewports.keys.some(key => hasOwnProperty(objectProp, key));

/**
 * Resolves a prop which may be a responsive object with keys for viewports.
 *
 * Used internally in useResponsiveProp - see that for more details.
 *
 * @param {*} prop
 * @param {"xs"|"sm"|"md"|"lg"|"xl"} viewport
 * @param {*} [defaultValue]
 * @returns {*}
 */
export const resolveResponsiveProp = (prop, viewport, defaultValue) => {
  if (prop === undefined) return defaultValue;
  if (!prop || typeof prop !== 'object' || !hasResponsiveProperties(prop)) return prop;
  const value = viewports.keys.includes(viewport) ?
  // If there's a current viewport, return the closest match at or below it
  viewports.inherit(prop)[viewport] :
  // If no current viewport is available, default to smallest viewport
  prop[viewports.keys.find(key => hasOwnProperty(prop, key))];
  return value === undefined || value === null ? defaultValue : value;
};

/**
 * Takes any value and, if that value is a responsive prop (an object with one or more
 * keys matching system viewport names), returns the value corresponding to the largest
 * viewport key smaller than the current screen's viewport.
 *
 * For example, if passed `{ sm: 4, lg: 8 }`, it will return 8 on `xl` and `lg` viewports,
 * 4 on `md` and `sm` viewports, and `undefined` (or a provided `defaultValue`) on `xs` viewports.
 *
 * To generate propTypes for responsive props, see `responsiveProps` in `./utils/props/responsiveProps.js`.
 *
 * @param {*} prop - any value which may be an object with viewport keys
 * @param {*} [defaultValue] - default value to provide if no matches found for the current viewport
 * @returns {*}
 */
const useResponsiveProp = (prop, defaultValue) => {
  const viewport = useViewport();
  return resolveResponsiveProp(prop, viewport, defaultValue);
};
export default useResponsiveProp;